// src/pages/HeathrowFareCompareCheapest.jsx
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import api from "../utils/api";
import "../styles/heathrowFareCompare.css";

const HeathrowFareCompareCheapest = () => {
  const [pickup, setPickup] = useState("");
  const [terminal, setTerminal] = useState("T5");
  const [datetime, setDatetime] = useState("");
  const [passengers, setPassengers] = useState(2);
  const [luggage, setLuggage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const autoSubmitted = useRef(false);

  useEffect(() => {
    const nextMondayAt10 = () => {
      const now = new Date();
      const day = now.getDay();
      const daysToMon = (8 - day) % 7 || 7;
      const target = new Date(now);
      target.setDate(now.getDate() + daysToMon);
      target.setHours(10, 0, 0, 0);
      const pad = n => String(n).padStart(2, "0");
      return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
    };
    setPickup("SW6 5HJ");
    setTerminal("T5");
    setPassengers(4);
    setLuggage(3);
    setDatetime(nextMondayAt10());
  }, []);

  useEffect(() => {
    if (autoSubmitted.current) return;
    if (pickup && terminal && datetime) {
      autoSubmitted.current = true;
      handleSubmit(new Event("submit"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, terminal, datetime]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setHasSubmitted(true);
    setResult(null);

    if (!pickup.trim()) {
      setError("Please enter a pickup postcode.");
      return;
    }
    if (!terminal) {
      setError("Please choose a Heathrow terminal.");
      return;
    }
    if (!datetime) {
      setError("Please choose a pickup date and time.");
      return;
    }

    const terminalPostcodes = {
      T2: "TW6 1EW",
      T3: "TW6 1QG",
      T4: "TW6 3XA",
      T5: "TW6 2GA"
    };
    const dropoffHeathrow = terminalPostcodes[terminal] || terminal;

    const payload = {
      pickup_postcode: pickup.trim(),
      dropoff_postcode: dropoffHeathrow,
      pickup_datetime: datetime,
      passengers: Number(passengers),
      luggage: Number(luggage)
    };

    try {
      setLoading(true);
      const res = await api.post("/api/fare-compare", payload);
      setResult(res.data);
    } catch (err) {
      const msg =
        err?.response?.data?.details ||
        err?.response?.data?.error ||
        "We could not generate a fare comparison for this route.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const renderMoney = value =>
    typeof value === "number" ? `£${value.toFixed(2)}` : "—";

  const journeyDistanceText =
    result?.journey &&
    `Distance: ~${result.journey.distance_miles} miles (${result.journey.distance_km} km)`;

  const benchmarkRangeText = range => {
    if (!range) return null;
    const midText = range.mid ? ` (typical ~${renderMoney(range.mid)})` : "";
    return `${renderMoney(range.min)} – ${renderMoney(range.max)}${midText}`;
  };

  const airportTaxiRange = result?.fares?.benchmarks?.airport_taxi?.saloon || null;
  const onlineTaxiRange = result?.fares?.benchmarks?.online_taxi?.saloon || null;
  const benchmarksCapturedAt = result?.fares?.benchmarks?.captured_at
    ? new Date(result.fares.benchmarks.captured_at).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })
    : null;

  const stressStrip = (title, bullets, stress, price) => (
    <div className="tool-provider-card" style={{ background: "#f8fafc", borderColor: "#e5e7eb" }}>
      <h3>{title}</h3>
      <ul style={{ marginTop: 6, marginBottom: 6, paddingLeft: 18, lineHeight: 1.5 }}>
        {bullets.map((b, idx) => (
          <li key={idx}>{b}</li>
        ))}
      </ul>
      <div style={{ fontSize: 13 }}>Price certainty: {stress} · Hassle: {price}</div>
    </div>
  );

  const bestForCard = (title, bullets) => (
    <div className="tool-provider-card" style={{ background: "#f8fafc", borderColor: "#e5e7eb" }}>
      <h3>{title}</h3>
      <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18, lineHeight: 1.5 }}>
        {bullets.map((b, idx) => (
          <li key={idx}>{b}</li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="tool-page">
      <Helmet>
        <title>Cheapest Taxi to Heathrow (Live Price Comparison)</title>
        <meta
          name="description"
          content="Find the cheapest taxi to Heathrow with live fixed fare vs app vs black cab estimates. Compare prices and see how surge, Reserve fees, and vehicle availability affect Uber vs fixed taxi options."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/cheapest-taxi-to-heathrow" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SoftwareApplication",
                name: "Cheapest Taxi to Heathrow Calculator",
                applicationCategory: "TravelApplication",
                operatingSystem: "Web",
                url: "https://www.cheapcabgrab.com/cheapest-taxi-to-heathrow",
                offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" }
              },
              {
                "@type": "FAQPage",
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "Is Uber always cheaper than a taxi to Heathrow?",
                    acceptedAnswer: { "@type": "Answer", text: "No. Surge pricing, Reserve fees, and limited larger vehicles can make fixed fares cheaper or more reliable, especially at peak times." }
                  },
                  {
                    "@type": "Question",
                    name: "Why are fixed taxi fares sometimes cheaper than Uber?",
                    acceptedAnswer: { "@type": "Answer", text: "Fixed fares avoid surge, include meet-and-greet, and are locked even if traffic or flight times shift." }
                  },
                  {
                    "@type": "Question",
                    name: "Why can Uber Reserve cost more than a fixed taxi?",
                    acceptedAnswer: { "@type": "Answer", text: "Reserve adds scheduling fees, and prices can still move with demand. Not all postcodes support Reserve, especially for larger vehicles." }
                  },
                  {
                    "@type": "Question",
                    name: "What’s the cheapest way to get to Heathrow with luggage?",
                    acceptedAnswer: { "@type": "Answer", text: "Pre-booked fixed fare taxis are usually cheapest once you include bags, walking, and surge risk." }
                  },
                  {
                    "@type": "Question",
                    name: "What’s the cheapest taxi for Heathrow Terminal 5?",
                    acceptedAnswer: { "@type": "Answer", text: "Fixed fare pre-booked taxis are typically the cheapest reliable option for T5, especially with luggage or groups." }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <main className="tool-container">
        <header className="tool-header">
          <h1>Cheapest Taxi to Heathrow (Live Price Comparison)</h1>
          <p className="tool-intro">
            Enter your pickup, passengers and bags to see the cheapest taxi options to Heathrow across fixed fares, ride-hailing, black cabs, and benchmark airport taxis.
          </p>
          <p style={{ fontSize: 12, color: "#475467" }}>Last Updated: 14 Jan 2026</p>
        </header>

        <section className="tool-grid">
          <article className="tool-card">
            <h2>Compare Heathrow Taxi Prices (Fixed vs Metered vs Apps)</h2>
            <form className="tool-form" onSubmit={handleSubmit}>
              <div className="tool-form-row">
                <label htmlFor="pickup">Pickup postcode</label>
                <input
                  id="pickup"
                  type="text"
                  value={pickup}
                  onChange={e => setPickup(e.target.value)}
                  placeholder="e.g. SW6 5HJ"
                />
              </div>

              <div className="tool-form-row">
              <label htmlFor="terminal">Heathrow terminal</label>
              <select id="terminal" value={terminal} onChange={e => setTerminal(e.target.value)}>
                <option value="T2">Terminal 2</option>
                <option value="T3">Terminal 3</option>
                <option value="T4">Terminal 4</option>
                <option value="T5">Terminal 5</option>
              </select>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                T2: TW6 1EW · T3: TW6 1QG · T4: TW6 3XA · T5: TW6 2GA
              </div>
            </div>

              <div className="tool-form-row">
                <label htmlFor="datetime">Pickup date &amp; time</label>
                <input
                  id="datetime"
                  type="datetime-local"
                  value={datetime}
                  onChange={e => setDatetime(e.target.value)}
                />
              </div>

              <div className="tool-form-row tool-form-row-inline">
                <div>
                  <label htmlFor="passengers">Passengers</label>
                  <input
                    id="passengers"
                    type="number"
                    min="1"
                    max="6"
                    value={passengers}
                    onChange={e => setPassengers(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="luggage">Large bags</label>
                  <input
                    id="luggage"
                    type="number"
                    min="0"
                    max="6"
                    value={luggage}
                    onChange={e => setLuggage(e.target.value)}
                  />
                </div>
              </div>

              {error && (
                <div className="tool-error" style={{ marginTop: 8 }}>
                  {error}
                </div>
              )}

              <button type="submit" className="primary-btn" disabled={loading} style={{ marginTop: 10 }}>
                {loading ? "Comparing..." : "Compare taxi prices"}
              </button>
            </form>
          </article>

          <article className="tool-card tool-results-card">
            <h2>Fare &amp; Hassle Comparison</h2>

            {!hasSubmitted && <p style={{ fontSize: 14 }}>Enter your route to see the cheapest taxi options across fixed fares, ride-hail apps, black cabs, and market benchmarks.</p>}
            {hasSubmitted && !loading && !result && !error && <p style={{ fontSize: 14 }}>No results yet. Please try again.</p>}
            {error && hasSubmitted && <p className="tool-error" style={{ marginTop: 8 }}>{error}</p>}

            {result && (
              <>
                <div className="tool-summary">
                  <h3>Journey Summary</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.5 }}>
                    <strong>From:</strong> {result.journey.pickup_postcode}
                    <br />
                    <strong>To:</strong> {result.journey.dropoff_postcode}
                    <br />
                    <strong>Pickup:</strong>{" "}
                    {new Date(result.journey.pickup_datetime).toLocaleString("en-GB", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false
                    })}
                    <br />
                    <strong>Passengers:</strong> {result.journey.passengers} · <strong>Bags:</strong> {result.journey.luggage}
                    <br />
                    {journeyDistanceText && <span><strong>{journeyDistanceText}</strong></span>}
                  </p>
                  <p style={{ marginTop: 6, fontSize: 13 }}>
                    If peace of mind matters more than saving a few pounds, fixed fares are usually the safest option for Heathrow journeys.
                  </p>
                  <p style={{ marginTop: 6, fontSize: 13 }}>
                    With {result.journey.passengers} passenger{result.journey.passengers > 1 ? "s" : ""} and {result.journey.luggage} large bag{result.journey.luggage === 1 ? "" : "s"}, ride-hailing availability can be limited during peak times.
                  </p>
                </div>

                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 10,
                    background: "#ecfdf3",
                    border: "1px solid #bbf7d0",
                    fontWeight: 600,
                    color: "#14532d",
                    fontSize: 14
                  }}
                >
                  🏆 Best overall for this journey: CheapCabGrab (fixed fare) — lowest risk, predictable price, suitable for groups and luggage.
                </div>

                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 10,
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    color: "#9a3412",
                    fontSize: 13
                  }}
                >
                  🚨 Last-minute booking warning (airport trips): Ride-hailing apps can show lower prices early, but availability and surge pricing often change close to departure — especially with luggage or groups.
                </div>

                {/* Price cards */}
                <h3 style={{ marginTop: 14 }}>
                  Price comparison{" "}
                  <span
                    aria-label="Why prices differ"
                    title="Ride-hailing prices vary due to surge, demand, and vehicle availability. Metered taxis vary with traffic and waiting time."
                    style={{ fontSize: 12, color: "#6b7280" }}
                  >
                    ℹ️
                  </span>
                </h3>
                <div className="tool-providers-grid">
                  {result.fares?.cheapcabgrab && (
                    <div className="tool-provider-card highlight" style={{ borderColor: "#0ea5e9", background: "#e0f2fe" }}>
                      <h3>CheapCabGrab (Fixed Taxi)</h3>
                      <p style={{ fontSize: 14 }}>
                        <strong>Estimated taxi fare:</strong>{" "}
                        <span className="provider-price ccg-price">
                          {renderMoney(result.fares.cheapcabgrab.one_way)}
                        </span>
                      </p>
                      <p style={{ fontSize: 13, marginTop: 4 }}>
                        Once booked, your price does not change — even if traffic or flight times shift.
                      </p>
                    </div>
                  )}

                  {result.fares?.ride_hail && (
                    <div className="tool-provider-card" style={{ borderColor: "#e5e7eb", background: "#f8fafc" }}>
                      <h3>Ride-hailing (Uber/Bolt)</h3>
                      <p style={{ fontSize: 14 }}>
                        <strong>Estimated range:</strong>{" "}
                        {renderMoney(result.fares.ride_hail.min)} – {renderMoney(result.fares.ride_hail.max)}
                      </p>
                      <p style={{ fontSize: 12, marginTop: 4 }}>
                        Looks cheaper early; not always cheaper at pickup. “Reserve” costs more and is not available in all areas.
                      </p>
                    </div>
                  )}

                  {result.fares?.black_cab && (
                    <div className="tool-provider-card" style={{ borderColor: "#e5e7eb", background: "#f8fafc" }}>
                      <h3>London Taxi / Black Cab</h3>
                      <p style={{ fontSize: 14 }}>
                        <strong>Estimated range:</strong>{" "}
                        {renderMoney(result.fares.black_cab.min)} – {renderMoney(result.fares.black_cab.max)}
                      </p>
                      <p style={{ fontSize: 12, marginTop: 4 }}>Metered; final fare depends on traffic/time.</p>
                    </div>
                  )}

                  {airportTaxiRange && (
                    <div className="tool-provider-card" style={{ borderColor: "#e5e7eb", background: "#f8fafc" }}>
                      <h3>Airport Taxi (Benchmark)</h3>
                      <p style={{ fontSize: 14 }}>
                        <strong>Typical one-way:</strong> {benchmarkRangeText(airportTaxiRange)}
                      </p>
                      {benchmarksCapturedAt && (
                        <p style={{ fontSize: 12, marginTop: 4, color: "#666" }}>
                          Reference quote — last updated {benchmarksCapturedAt}
                        </p>
                      )}
                    </div>
                  )}

                  {onlineTaxiRange && (
                    <div className="tool-provider-card" style={{ borderColor: "#e5e7eb", background: "#f8fafc" }}>
                      <h3>Online Taxi (Benchmark)</h3>
                      <p style={{ fontSize: 14 }}>
                        <strong>Typical one-way:</strong> {benchmarkRangeText(onlineTaxiRange)}
                      </p>
                      {benchmarksCapturedAt && (
                        <p style={{ fontSize: 12, marginTop: 4, color: "#666" }}>
                          Reference quote — last updated {benchmarksCapturedAt}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Stress cards */}
                <h3 style={{ marginTop: 14 }}>Price certainty &amp; hassle</h3>
                <div className="tool-providers-grid">
                  {stressStrip("CheapCabGrab", ["✅ Price locked", "✅ Vehicle guaranteed", "✅ Pre-booked driver", "⚠️ Slightly higher than off-peak app fares"], "🟢 High", "🟢 Low")}
                  {stressStrip("Ride-hailing apps", ["⚠️ May surge", "⚠️ Vehicle not guaranteed", "⚠️ Airport pickup delays possible"], "🟠 Variable", "🟠 Medium")}
                  {stressStrip("Black cab", ["✅ Licensed + bus lane access", "⚠️ Final price depends on meter/time in traffic"], "🟠 Variable", "🟠 Medium")}
                  {stressStrip("Airport taxi (benchmark)", ["✅ Pre-booked transfer", "⚠️ May add parking/wait fees", "⚠️ Quality varies by operator"], "🟠 Medium", "🟠 Varies")}
                </div>

                {/* Best for */}
                <h3 style={{ marginTop: 14 }}>Best for…</h3>
                <div className="tool-providers-grid">
                  {bestForCard("CheapCabGrab", ["Groups or luggage", "Early flights / peace of mind", "Door-to-door, no surge"])}
                  {bestForCard("Ride-hailing", ["Solo, off-peak", "Flexible timing", "Lower upfront, higher risk"])}
                  {bestForCard("Black cab", ["Walk-up city trips", "Fast bus-lane access", "Short notice"])}
                  {bestForCard("Airport taxi (benchmark)", ["Standard airport transfer operators", "Passengers who want a reference quote", "May include parking/meet fees"])}
                </div>

                {/* Takeaways */}
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 10,
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    fontSize: 13
                  }}
                >
                  <strong>Quick takeaway (copy/paste):</strong>
                  <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                    <li>Uber looks cheaper early; Reserve fees and surge can make it higher at pickup.</li>
                    <li>Fixed fares cost more upfront, less stress later.</li>
                    <li>Groups + luggage benefit most from pre-booking.</li>
                  </ul>
                </div>

                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 10,
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                    fontSize: 13
                  }}
                >
                  <strong>Why Uber can be more expensive at times:</strong>
                  <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                    <li>Reserve adds booking fees and is not available everywhere (especially for larger vehicles).</li>
                    <li>Surge pricing near pickup can exceed fixed fares, particularly for early flights.</li>
                    <li>Limited XL vehicles for luggage can force premium options or cancellations.</li>
                  </ul>
                </div>

                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    borderRadius: 10,
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    fontSize: 13
                  }}
                >
                  <strong>Why taxi can cost more than train:</strong>
                  <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                    <li>Door-to-door, no walking or changes.</li>
                    <li>Includes luggage handling and meet-and-greet.</li>
                    <li>Night/early morning availability; vehicle guaranteed.</li>
                  </ul>
                </div>

                <div className="tool-disclaimer">
                  <p style={{ fontSize: 12, color: "#555" }}>
                    These fare ranges are estimates, not live quotes. Verify exact fares in the app, taxi meter, or booking before travel.
                  </p>
                </div>

                <div style={{ marginTop: 16 }}>
                  <strong>Plan your Heathrow journey:</strong>
                  <ul style={{ marginTop: 6, fontSize: 14, lineHeight: 1.6 }}>
                    <li>🚖 <Link to="/heathrow-fare-compare">Compare taxi fares</Link></li>
                    <li>🅿️ <Link to="/heathrow-parking-vs-taxi">Parking vs taxi</Link></li>
                    <li>✈️ <Link to="/flight-checker">Check your flight</Link></li>
                    <li>🕒 <Link to="/leave-for-heathrow">When to leave for Heathrow</Link></li>
                    <li>🧭 <Link to="/terminalFinder">Find your terminal</Link></li>
                    <li>🧳 <Link to="/arrival-assistant">Arrival assistant</Link></li>
                  </ul>
                </div>
              </>
            )}
          </article>
        </section>

        <section className="tool-section tool-explainer" style={{ marginTop: 24 }}>
          <h2>How we calculate Heathrow taxi prices</h2>
          <p>
            We compare fixed fares, ride-hail estimates, metered taxi ranges, and benchmark airport/online taxi quotes using road distance, typical timing, and recent market data.
          </p>
          <p>
            Fixed fares stay the same once booked, even if traffic changes. Ride-hail and metered fares can rise with traffic, time, and surge/Reserve fees. Prices shown are estimates to help you decide quickly.
          </p>
        </section>

        <section className="tool-section" style={{ marginTop: 24 }}>
          <h2>FAQs</h2>
          <ul style={{ lineHeight: 1.6, paddingLeft: 18 }}>
            <li><strong>Is Uber always cheaper than a taxi to Heathrow?</strong> No. Surge pricing, Reserve fees, and limited larger vehicles can make fixed fares cheaper and more reliable.</li>
            <li><strong>Why are fixed taxi fares sometimes cheaper than Uber?</strong> Fixed fares avoid surge, include meet-and-greet, and are locked even if traffic changes.</li>
            <li><strong>Why can Uber Reserve cost more than a fixed taxi?</strong> Reserve adds scheduling fees, can still surge, and is not available in all postcodes, especially for larger vehicles.</li>
            <li><strong>What’s the cheapest way to get to Heathrow with luggage?</strong> Pre-booked fixed fare taxis are usually best when you include bags, walking, and surge risk.</li>
            <li><strong>What’s the cheapest taxi for Terminal 5?</strong> Fixed fare, pre-booked taxis are typically the cheapest reliable option for T5 with luggage.</li>
          </ul>
        </section>

        <section className="tool-section" style={{ marginTop: 24 }}>
          <h2>Customer Reviews</h2>
          <ReviewsBar />
        </section>
      </main>
    </div>
  );
};

export default HeathrowFareCompareCheapest;
